import { Component, Input, ViewChild } from '@angular/core';
import { EditInvoiceIntervalComponent } from 'components/accounting/ledger/edit-invoice-interval.component';
import { LedgerPostsComparisonComponent } from 'components/accounting/ledger/ledger-posts-comparison.component';
import { LedgerPostsComponent } from 'components/accounting/ledger/ledger-posts.component';
import { CompanyProductsLedgerPostsSaveComponent } from 'components/company/company-products-ledger-posts-save.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { CredentialType } from 'enums/CredentialType';
import { BillingPlanDtoModel } from 'models/generated/Accounting.Api.Models.BillingPlanDto';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { ProductPlanService } from 'services/productplan.service';

@Component({
	selector: 'company-products-ledger-posts',
	templateUrl: './company-products-ledger-posts.component.html',
	providers: [FormValidationService],
	imports: [Layout, LedgerPostsComponent, TranslatePipe]
})
export class CompanyProductsLedgerPostsComponent {
	@ViewChild('saveBillingPlan') saveButton?: SubmitButtonComponent;

	@Input() displayProductName = true;
	@Input() showClosedPosts = true;
	@Input() splitByKey?: string;
	
	@Input()
	set accountingPlanId(value: string | undefined) {
		if (this.accountingPlan == undefined) {
			this.accountingService.getAccountingPlan(value!).then(accountingPlan => {
				this.productPlanService.getProductPlans([accountingPlan.productPlanId!]).then(productPlans => {
					this.accountingPlan = accountingPlan;
					this.productPlans = productPlans;
				});
			});
		}
	}
	get accountingPlanId() { return this._accountingPlan?.Id; }

	private _accountingPlan?: AccountingPlanModel;
	@Input()
	set accountingPlan(plan: AccountingPlanModel | undefined) {
		this._accountingPlan = plan;
		if (plan) {
			this.loadHistoricalDates();
		}
	}
	get accountingPlan() { return this._accountingPlan; }

	private _productPlans: ProductPlanModel[] = [];
	@Input() set productPlans(value: ProductPlanModel[]) {
		this._productPlans = value;
		this.editMode = false;
	}
	get productPlans() { return this._productPlans; }

	public credentialTypeEnum = CredentialType;
	public editMode = false;
	public editMigratedData = false;

	public historicalTimestamps: Date[] = [];

	@ViewChild(LedgerPostsComponent)
	private ledgerPosts!: LedgerPostsComponent;

	constructor(private readonly accountingService: AccountingService, private readonly navigation: NavigationService, private readonly productPlanService: ProductPlanService) {
	}

	editLedgerPosts() {
		if (this.editMigratedData) this.resetEditMode().then(() => this.editMode = true);
		else this.editMode = true;
	}

	editInvoiceInterval() {
		this.navigation.openPopup(EditInvoiceIntervalComponent, 'ledger-posts.edit-interval.popup-header').then(view => {
			view.accountingPlan = this.accountingPlan;
			view.productPlan = this.productPlans[0];
		});
	}

	editMigratedLedgerPosts() {
		if (this.editMode) this.resetEditMode().then(() => this.editMigratedData = true);
		else this.editMigratedData = true;
	}

	loadHistoricalDates() {
		this.historicalTimestamps = [];
		if (!this.accountingPlan) return;
		this.accountingService.getHistoricalChangeDates(this.accountingPlan.id!).then(timestamps => {
			if (timestamps.length === 0) return;
			this.historicalTimestamps = timestamps;
		});
	}

	compareLedgerPosts() {
		this.navigation.openPopup(LedgerPostsComparisonComponent, 'ledger-posts.compare.popup-header').then(view => {
			view.changedTimestamps = this.historicalTimestamps;
			view.productPlans = this.productPlans;
			view.accountingPlan = this.accountingPlan;
		});
	}

	saveLedgerPosts() {
		if (this.accountingPlan?.totalPlanValue == this.ledgerPosts.totalLedgerValue) {
			this.doSave();
		}
		else {
			this.navigation.openPopup(CompanyProductsLedgerPostsSaveComponent, 'company-products-ledger-posts.confirm-tcv-changes').then(view => {
				view.newTcv = this.ledgerPosts.totalLedgerValue;
				view.accountingPlan = this.accountingPlan!;				
				view.productPlan = this.productPlans.find(pp => pp.id === this.accountingPlan?.productPlanId);
				view.checkMismatchOfAcvAndAnnualRenewalValue();
				view.result.subscribe(result => {
					if (result) this.doSave();
					else if (this.saveButton) this.saveButton.reset();
				});
			})
		}
	}
	private doSave() {
		let billingPlan = new BillingPlanDtoModel();
		billingPlan.accountingPlanId = this.accountingPlan!.id;
		billingPlan.ledgerPosts = this.ledgerPosts.getEditedLedgerPosts();
		billingPlan.expectedTotalValue = this.ledgerPosts.totalLedgerValue;
		if (this.editMigratedData)
			this.accountingService.updateMigratedLedgerPostAndTotalValue(billingPlan)
				.then(() => { this.resetEditMode(); })
				.catch(() => this.resetEditMode());
		else if (this.editMode)
			this.accountingService.updateBillingPlanAndTotalValue(billingPlan)
				.then(() => { this.resetEditMode(); })
				.catch(() => this.resetEditMode());
	}

	resetEditMode() : Promise<void> {
		this.editMode = false;
		this.editMigratedData = false;
		return this.ledgerPosts.reloadLedgerPosts();
	}
}
